import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { Building2, Server, Network, Shield, Users, Cpu } from 'lucide-react'

const infrastructure = [
  {
    icon: Building2,
    title: 'Office Infrastructure',
    details: [
      'Headquarters: Pune (50,000 sq. ft.)',
      'Regional Offices: 6 locations',
      'Development Centers: 3',
      'Training Facilities: 5',
    ],
  },
  {
    icon: Server,
    title: 'Data Centers',
    details: [
      'Primary DC: Pune (Tier-III)',
      'Disaster Recovery: Hyderabad',
      '99.99% Uptime SLA',
      '24/7 NOC & SOC',
    ],
  },
  {
    icon: Network,
    title: 'Network Infrastructure',
    details: [
      'Redundant Internet Leased Lines',
      'SD-WAN across all offices',
      '10 Gbps Backbone',
      'Multi-cloud Connectivity',
    ],
  },
  {
    icon: Shield,
    title: 'Security Infrastructure',
    details: [
      '24/7 Surveillance Systems',
      'Biometric Access Control',
      'CCTV with AI Analytics',
      'Firearms-trained Security',
    ],
  },
  {
    icon: Users,
    title: 'Development Facilities',
    details: [
      'Agile Workspaces',
      'Collaboration Zones',
      'Innovation Labs',
      'Client Demo Centers',
    ],
  },
  {
    icon: Cpu,
    title: 'R&D Infrastructure',
    details: [
      'AI/ML Lab',
      'IoT Testing Facility',
      'Hardware Prototyping',
      'Usability Testing Lab',
    ],
  },
]

const Infrastructure = () => {
  return (
    <>
      <Helmet>
        <title>Infrastructure | Eonixa Dynamics</title>
        <meta name="description" content="World-class infrastructure - 50,000 sq. ft. HQ, Tier-III Data Centers, R&D Labs, and Development Facilities" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-20">
        <div className="section-container">
          <FadeIn>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900">Infrastructure</h1>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                World-class infrastructure supporting our operations across India.
                Built for scalability, security, and innovation.
              </p>
            </div>
          </FadeIn>

          {/* Infrastructure Stats */}
          <FadeIn delay={0.2}>
            <div className="grid gap-8 sm:grid-cols-4 mb-16">
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-gray-900">50K+</p>
                <p className="mt-2 text-gray-600">Sq. Ft. HQ</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-gray-900">15+</p>
                <p className="mt-2 text-gray-600">Office Locations</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-gray-900">99.99%</p>
                <p className="mt-2 text-gray-600">Uptime SLA</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-gray-900">24/7</p>
                <p className="mt-2 text-gray-600">NOC & SOC</p>
              </div>
            </div>
          </FadeIn>

          {/* Infrastructure Details */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {infrastructure.map((item, index) => {
              const Icon = item.icon
              return (
                <FadeIn key={item.title} delay={index * 0.1}>
                  <div className="group h-full rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-card">
                    <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-primary-50 text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mb-4 text-lg font-bold text-gray-900">
                      {item.title}
                    </h3>
                    <ul className="space-y-2">
                      {item.details.map((detail) => (
                        <li
                          key={detail}
                          className="flex items-start gap-2 text-sm text-gray-600"
                        >
                          <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeIn>
              )
            })}
          </div>

          <FadeIn delay={0.6} className="mt-16">
            <div className="rounded-2xl bg-gradient-to-r from-primary-900 to-primary-700 p-12 text-center text-white">
              <h2 className="mb-4 text-2xl font-bold">Investment in Infrastructure</h2>
              <p className="mx-auto max-w-2xl text-gray-200">
                We've invested ₹50+ Crores in building world-class infrastructure
                that supports our growth and enables us to deliver excellence to
                our clients.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </>
  )
}

export default Infrastructure
